<script setup lang="ts">
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { BaseFile } from "~/classes/file/BaseFile"
import { ReactiveFile } from "~/classes/file/ReactiveFile"

const props = defineProps({
  transferId: null
})
const { uploadJob } = useJobs()
const { clearNotificationById, closeNotificationTray } = useNotificationTray()
const upload = uploadJob(props.transferId)

if (!isDefined(upload))
  throw new Error(`Upload job with transferId ${props.transferId} not found`)

const job = upload.state
const totalFiles = upload.totalFiles
const totalBytes = upload.totalBytes
const countedBytes = upload.countedBytes
const preparedBytes = upload.preparedBytes
const uploadingBytes = upload.uploadingBytes
const uploadedBytes = upload.uploadedBytes
const failedBytes = upload.failedBytes
const completedBytes = upload.completedBytes
const finishedBytes = upload.finishedBytes

// Create a reactive file for the destination so we can retrieve its name, etc.
const destination = ReactiveFile.fromFileId(
  upload.state.value.folderId,
  InterfaceSelection.minimumFileItemSelection(),
  "tray",
  { immediate: true },
).ref()

const destinationName = computed(() => destination.value.name())
</script>

<template>
  <div class="container">
    <div class="preview">
      <nuxt-link
        :to="BaseFile.getFolderPath(destination.id)"
        @click="closeNotificationTray()"
      >
        <file-thumbnail
          class="file-thumbnail"
          :file="destination"
          :container-height="40"
          :container-width="40"
          hide-stale
        />
      </nuxt-link>
    </div>
    <div class="content">
      <div class="info">
        <div class="info-left">
          <template v-if="job.title">
            {{ job.title }}
          </template>
          <template v-else-if="job.status === 'complete'">
            <template v-if="totalFiles > 1">
              Uploaded {{ totalFiles }} files
            </template>
            <template v-else>
              <div
                v-for="file in job.files"
                :key="file.fileId"
                class="info-file-title"
              >
                <span class="info-file-link-prefix">
                  Uploaded
                </span>
                <nuxt-link
                  v-if="file.isFolder"
                  :to="BaseFile.getFolderPath(file.fileId)"
                  class="info-file-link"
                  @click="closeNotificationTray()"
                >
                  {{ file.name }}
                </nuxt-link>
                <nuxt-link
                  v-else
                  :to="BaseFile.getFilePath(file.fileId)"
                  class="info-file-link"
                  @click="closeNotificationTray()"
                >
                  {{ file.name }}
                </nuxt-link>
              </div>
            </template>
          </template>
          <template v-else-if="job.status === 'counting'">
            Reading files...
          </template>
          <template v-else-if="job.status === 'preparing'">
            Allocating (this takes a while)...
          </template>
          <template v-else-if="totalFiles === 1">
            Uploading file...
          </template>
          <template v-else>
            Uploading {{ totalFiles }} files...
          </template>
        </div>
        <div class="info-right">
          <!-- TODO: implement cancellation -->
          <div
            v-if="job.status !== 'complete'"
            class="info-right-cancel"
            @click.stop.prevent="clearNotificationById(transferId)"
          >
            Cancel
          </div>
          <div
            v-else
            class="info-right-cancel"
            @click.stop.prevent="clearNotificationById(transferId)"
          >
            Clear
          </div>
        </div>
      </div>
      <div class="progress-bar">
        <div class="background" />
        <div
          class="part listed"
          :style="{ width: fracToPct((countedBytes + preparedBytes + uploadingBytes + uploadedBytes + failedBytes + completedBytes) / totalBytes) }"
        />
        <div
          class="part prepared"
          :style="{ width: fracToPct((preparedBytes + uploadingBytes + uploadedBytes + failedBytes + completedBytes) / totalBytes) }"
        />
        <div
          class="part finished"
          :style="{ width: fracToPct(finishedBytes / totalBytes) }"
        />
        <div
          class="part failed"
          :style="{ width: fracToPct((completedBytes + failedBytes) / totalBytes) }"
        />
        <div
          class="part success"
          :style="{ width: fracToPct(completedBytes / totalBytes) }"
        />
      </div>
      <div class="action">
        <div class="action-left">
          <div v-if="['counting'].includes(job.status)" class="action-detail ">
            <icon-action type="arrow-up" class="action-icon blue" />
            <span>Calculating...</span>
          </div>
          <div v-else-if="['uploading'].includes(job.status)" class="action-detail">
            <icon-action type="arrow-up" class="action-icon blue" />
            <span class="action-destination-prefix">Uploading</span>
            <span v-if="destinationName" class="action-destination-prefix">to</span>
            <nuxt-link
              v-if="destinationName"
              :to="BaseFile.getFolderPath(destination.id)"
              class="action-destination-link"
              @click="closeNotificationTray()"
            >
              {{ destinationName }}
            </nuxt-link>
          </div>
          <div v-else-if="job.status === 'complete'" class="action-detail ">
            <icon-action type="check" class="action-icon green" />
            <span class="action-destination-prefix">Uploaded</span>
            <span v-if="destinationName" class="action-destination-prefix">to</span>
            <nuxt-link
              v-if="destinationName"
              :to="BaseFile.getFolderPath(destination.id)"
              class="action-destination-link"
              @click="closeNotificationTray()"
            >
              {{ destinationName }}
            </nuxt-link>
          </div>
        </div>
        <div class="action-right">
          <span>{{ formatBytes(finishedBytes, 2) }}</span>
          <span>of</span>
          <span>{{ formatBytes(totalBytes, 2) }}</span>
        </div>
      </div>
    </div>
    <!-- <div class="close" @click.stop.prevent="clearNotificationById(job.id)">
      <menu-action-icon type="cross" />
    </div> -->
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.container
  @apply flex px-[#{$menu-tray-px-item}] py-[#{$menu-tray-py-item}] gap-[#{$menu-tray-gap-item}] items-center w-full

.preview
  @apply flex-none items-center justify-center flex
.preview-icon
  @apply h-full w-full

.content
  @apply flex flex-col gap-[6px] w-full min-w-0
.info
  @apply flex break-words gap-2
.info-left
  @apply break-all break-words overflow-hidden w-full text-ellipses-after-2-lines
.info-file-title
  @apply flex gap-1 min-w-0 overflow-hidden
.info-file-link-prefix
  @apply flex-none text-[#{$global-t-default}]
.info-file-link
  @apply text-[#{$global-t-default}] text-ellipses-inline min-w-0
  &:hover
    @apply underline underline-[#{$global-t-quaternary}]
.info-right
  @apply flex flex-none ml-auto t-menu-xs text-[#{$menu-tray-t-accent}] items-center
.info-right-cancel
  @apply hover:bg-[#{$menu-bg-item-hover}] p-1 -m-1 rounded-[#{$menu-br-item}] cursor-pointer
.progress-bar
  @apply h-[#{$menu-tray-h-progress}]
  @apply relative w-full bg-[#{$menu-tray-bg-progress}] rounded-full overflow-hidden
.part
  @apply absolute top-0 left-0 h-full
  &.listed
    @apply bg-$global-steel-accent
  &.prepared
    @apply bg-$global-steel-primary
  &.failed
    @apply bg-$global-red-primary
  &.finished
    @apply bg-$global-blue-secondary
  &.success
    @apply bg-$global-green-primary

.action
  @apply flex items-center
  @apply text-[#{$global-fs-sm}]
  @apply text-[#{$global-t-secondary}]
  @apply fw-[450]
  @apply gap-2

.action-left
  @apply flex items-center w-full min-w-0 overflow-hidden
.action-detail
  @apply flex gap-1 overflow-hidden min-w-0
.action-destination-prefix
  @apply text-[#{$global-t-quaternary}] flex-none
.action-destination-link
  @apply text-[#{$global-t-secondary}] text-ellipses-inline min-w-0
  &:hover
    @apply underline underline-[#{$global-t-quaternary}]
.action-right
  @apply flex ml-auto gap-0.75 flex-none
.action-icon
  @apply rounded-full flex-none
  &.blue
    @apply text-$global-blue-primary
  &.green
    @apply text-$global-green-primary
</style>
